import axios from 'axios';

// En production (servi par Express), URL relative. En dev, override possible.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Instance Axios avec intercepteur JWT
const api = axios.create({
  baseURL: API_BASE_URL
});

// Intercepteur : ajouter Authorization Bearer si token présent
api.interceptors.request.use(config => {
  const token = localStorage.getItem('assurextract_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur : si 401, supprimer le token invalide
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('assurextract_token');
      localStorage.removeItem('assurextract_user');
      window.dispatchEvent(new Event('auth-required'));
    }
    return Promise.reject(error);
  }
);

/**
 * Stocker le token JWT et l'utilisateur
 */
export function setAuth(token, user) {
  if (token) {
    localStorage.setItem('assurextract_token', token);
    if (user) localStorage.setItem('assurextract_user', JSON.stringify(user));
  } else {
    localStorage.removeItem('assurextract_token');
    localStorage.removeItem('assurextract_user');
  }
}

/**
 * Récupérer le token stocké
 */
export function getToken() {
  return localStorage.getItem('assurextract_token');
}

/**
 * Récupérer l'utilisateur stocké
 */
export function getStoredUser() {
  try {
    const u = localStorage.getItem('assurextract_user');
    return u ? JSON.parse(u) : null;
  } catch { return null; }
}

/**
 * Récupérer le profil depuis le serveur
 */
export async function getMe() {
  const response = await api.get('/auth/me', { timeout: 5000 });
  return response.data;
}

/**
 * Upload et traitement d'UN SEUL fichier PDF
 */
export async function uploadSingleFile(file, syndicData = null) {
  try {
    const formData = new FormData();
    formData.append('files', file);

    if (syndicData) {
      formData.append('syndicData', JSON.stringify(syndicData));
    }

    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Extraction LLM : un gros relevé multi-volets (beaucoup de sinistres) peut prendre
      // plusieurs minutes à générer → 10 min pour ne pas abandonner avant la fin du serveur.
      timeout: 600000
    });

    return response.data;
  } catch (error) {
    console.error(`Erreur upload ${file.name}:`, error);
    throw new Error(
      error.response?.data?.error ||
      error.message ||
      `Erreur lors du traitement de ${file.name}`
    );
  }
}

/**
 * Export des données vers Excel
 */
export async function exportToExcel(data) {
  try {
    const response = await api.post('/export/excel', { data }, {
      timeout: 60000
    });

    if (response.data.success) {
      // Construire l'URL de téléchargement (relative)
      const baseUrl = API_BASE_URL === '/api' ? '' : API_BASE_URL.replace('/api', '');
      const downloadUrl = response.data.downloadUrl.startsWith('http')
        ? response.data.downloadUrl
        : `${baseUrl}${response.data.downloadUrl}`;

      return { ...response.data, downloadUrl };
    }

    return response.data;
  } catch (error) {
    console.error('Erreur export Excel:', error);
    throw new Error(
      error.response?.data?.error ||
      error.message ||
      'Erreur lors de l\'export Excel'
    );
  }
}

/**
 * Finaliser après validation : renommage + rangement dans la GED avec les données corrigées
 */
export async function finalizeDocuments(data, uploadedFiles, riResults = null) {
  try {
    // riResults : détail renvoyé par le workflow RI (sinistres créés / doublons), repris dans
    // l'email récapitulatif unique.
    const response = await api.post('/finalize', { data, uploadedFiles, riResults }, {
      // Finalisation : résolution copro via contrat (1 appel/RI) + attente des contrats tout
      // juste créés (jusqu'à 90 s) + dépôts GED.
      timeout: 300000
    });
    return response.data;
  } catch (error) {
    console.error('Erreur finalisation:', error);
    throw new Error(
      error.response?.data?.error ||
      error.message ||
      'Erreur lors de la finalisation'
    );
  }
}

/**
 * Envoyer les données vers n8n pour intégration Airtable
 */
export async function sendToN8n(data, metadata = {}) {
  try {
    const response = await api.post('/webhook/n8n', { data, metadata }, {
      // Le workflow RI répond en fin de traitement (il attend 30 s puis crée les sinistres),
      // un RI après l'autre : laisser le temps à plusieurs RI.
      timeout: 300000
    });
    return response.data;
  } catch (error) {
    console.error('Erreur envoi n8n:', error);
    throw new Error(
      error.response?.data?.error ||
      error.message ||
      'Erreur lors de l\'envoi vers n8n'
    );
  }
}

/**
 * [V2] Recherche les candidats existants (immeuble / contrat / client / contact) par document,
 * via le proxy backend → webhook n8n de matching déterministe.
 * Renvoie un tableau aligné sur `documents` : { docId, immeuble[], contrat[], client[], contact[] }.
 */
export async function searchCandidates(documents) {
  try {
    const docs = Array.isArray(documents) ? documents : [documents];
    const response = await api.post('/match/candidates', { documents: docs }, { timeout: 60000 });
    return response.data?.candidates || [];
  } catch (error) {
    console.error('Erreur recherche candidats:', error);
    throw new Error(
      error.response?.data?.error || error.message || 'Erreur lors de la recherche de candidats'
    );
  }
}

/**
 * Recherche manuelle d'un client ou d'un immeuble Airtable (quand le rapprochement
 * automatique n'a rien trouvé — ex. RI sans adresse extraite).
 * @param {'immeuble'|'client'} type
 * @param {string} query
 */
export async function searchEntity(type, query) {
  try {
    const response = await api.post('/entity-search', { type, query: query || '' }, { timeout: 20000 });
    return response.data?.candidates || [];
  } catch (error) {
    console.error('Erreur recherche entité:', error);
    return [];
  }
}

/**
 * Enregistrer des corrections (learning)
 */
export async function saveCorrections(corrections) {
  if (!corrections || corrections.length === 0) return;
  try {
    await api.post('/corrections', { corrections });
  } catch (err) {
    console.error('Erreur enregistrement corrections:', err);
  }
}

/**
 * Recherche assistée de contrats Airtable (pour lier un RI quand le contrat n'est pas trouvé)
 * @param {string} numero - numéro de contrat (exact ou partiel)
 * @param {string} query - texte libre (nom de copro, etc.)
 */
export async function searchContrat(numero, query) {
  try {
    const response = await api.post('/ri/contrat-search', { numero: numero || '', query: query || '' }, { timeout: 20000 });
    return response.data?.candidates || [];
  } catch (error) {
    console.error('Erreur recherche contrat:', error);
    return [];
  }
}

/**
 * Health check de l'API
 */
export async function healthCheck() {
  try {
    const response = await api.get('/health', { timeout: 5000 });
    return response.data;
  } catch (error) {
    console.error('API non disponible:', error);
    return { status: 'error', message: 'API non disponible' };
  }
}

/**
 * Recherche d'entreprise via l'API Recherche Entreprises (api.gouv.fr)
 */
export async function searchEntreprise(query) {
  try {
    if (!query || query.trim().length < 2) return [];

    const encodedQuery = encodeURIComponent(query.trim());
    const response = await axios.get(
      `https://recherche-entreprises.api.gouv.fr/search?q=${encodedQuery}&page=1&per_page=10`,
      { timeout: 10000 }
    );

    if (response.data && response.data.results) {
      return response.data.results.map(entreprise => {
        const numeroVoie = entreprise.siege?.numero_voie || '';
        const typeVoie = entreprise.siege?.type_voie || '';
        const libelleVoie = entreprise.siege?.libelle_voie || '';
        const adresseRue = [numeroVoie, typeVoie, libelleVoie].filter(Boolean).join(' ').trim();

        return {
          siret: entreprise.siege?.siret || '',
          siren: entreprise.siren || '',
          nom: entreprise.nom_complet || entreprise.nom_raison_sociale || '',
          adresse: adresseRue,
          codePostal: entreprise.siege?.code_postal || '',
          ville: entreprise.siege?.libelle_commune || '',
          codeNaf: entreprise.activite_principale || '',
          libelleNaf: entreprise.libelle_activite_principale || '',
          categorieJuridique: entreprise.nature_juridique || '',
          dateCreation: entreprise.date_creation || ''
        };
      });
    }

    return [];
  } catch (error) {
    console.error('Erreur recherche entreprise:', error);
    return [];
  }
}
